/**
 * Image Compression Service
 * 
 * This service provides image compression functionality that can be used
 * throughout the application. It handles both browser-side compression
 * using canvas API and maintains quality while reducing file size.
 */

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  format?: 'jpeg' | 'webp' | 'png';
  maintainAspectRatio?: boolean;
}

export interface CompressionResult {
  file: File;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  width: number;
  height: number;
}

const DEFAULT_OPTIONS: CompressionOptions = {
  maxWidth: 1920,
  maxHeight: 1920,
  quality: 0.85,
  format: 'jpeg',
  maintainAspectRatio: true
};

// Image size presets for different use cases
export const IMAGE_PRESETS = {
  thumbnail: {
    maxWidth: 600,  // Increased for better quality at display sizes
    maxHeight: 600, // Increased for better quality at display sizes
    quality: 0.95,  // High quality for sharpness
    format: 'jpeg' as const
  },
  reference: {
    maxWidth: 1400,  // Slightly increased
    maxHeight: 1400, // Slightly increased
    quality: 0.95,   // Increased to 95% for sharpness
    format: 'jpeg' as const
  },
  document: {
    maxWidth: 2000,  // Slightly increased
    maxHeight: 2000, // Slightly increased
    quality: 0.96,   // Near maximum quality
    format: 'jpeg' as const
  },
  highQuality: {
    maxWidth: 3000,  // Increased
    maxHeight: 3000, // Increased
    quality: 0.98,   // Nearly lossless
    format: 'jpeg' as const
  }
} as const;

export class ImageCompressionService {
  /**
   * Compress a single image file
   */
  static async compressImage(
    file: File,
    options: CompressionOptions = {}
  ): Promise<CompressionResult> {
    // Merge options with defaults
    const opts = { ...DEFAULT_OPTIONS, ...options };

    // Skip compression for non-image files
    if (!file.type.startsWith('image/')) {
      throw new Error('File is not an image');
    }

    // For small files (< 150KB), skip compression entirely to preserve quality
    if (file.size < 150 * 1024) {
      // Get image dimensions without compression
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          URL.revokeObjectURL(img.src); // Clean up
          resolve({
            file,
            originalSize: file.size,
            compressedSize: file.size,
            compressionRatio: 1,
            width: img.width,
            height: img.height
          });
        };
        img.onerror = () => {
          URL.revokeObjectURL(img.src); // Clean up
          resolve({
            file,
            originalSize: file.size,
            compressedSize: file.size,
            compressionRatio: 1,
            width: 0,
            height: 0
          });
        };
        img.src = URL.createObjectURL(file);
      });
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const img = new Image();

        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            if (!ctx) {
              throw new Error('Failed to get canvas context');
            }

            // Calculate new dimensions
            const { width, height } = this.calculateDimensions(
              img.width,
              img.height,
              opts.maxWidth!,
              opts.maxHeight!,
              opts.maintainAspectRatio!
            );

            canvas.width = width;
            canvas.height = height;

            // Apply image smoothing for better quality
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';

            // Draw the image on canvas
            ctx.drawImage(img, 0, 0, width, height);

            // Convert canvas to blob
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  reject(new Error('Failed to compress image'));
                  return;
                }

                // Create a new File from the blob
                const compressedFile = new File(
                  [blob],
                  this.generateCompressedFilename(file.name, opts.format!),
                  { type: `image/${opts.format}` }
                );

                resolve({
                  file: compressedFile,
                  originalSize: file.size,
                  compressedSize: compressedFile.size,
                  compressionRatio: compressedFile.size / file.size,
                  width,
                  height
                });
              },
              `image/${opts.format}`,
              opts.quality
            );
          } catch (error) {
            reject(error);
          }
        };

        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target?.result as string;
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  /**
   * Compress multiple images in parallel
   */
  static async compressImages(
    files: File[],
    options: CompressionOptions = {},
    onProgress?: (completed: number, total: number) => void
  ): Promise<CompressionResult[]> {
    const results: CompressionResult[] = [];
    let completed = 0;

    // Process images in batches to avoid memory issues
    const batchSize = 3;
    for (let i = 0; i < files.length; i += batchSize) {
      const batch = files.slice(i, i + batchSize);
      const batchResults = await Promise.all(
        batch.map(file => this.compressImage(file, options))
      );
      
      results.push(...batchResults);
      completed += batch.length;
      
      if (onProgress) {
        onProgress(completed, files.length);
      }
    }

    return results;
  }

  /**
   * Calculate new dimensions while maintaining aspect ratio
   * IMPORTANT: Never upscale images - only downscale when needed
   */
  private static calculateDimensions(
    originalWidth: number,
    originalHeight: number,
    maxWidth: number,
    maxHeight: number,
    maintainAspectRatio: boolean
  ): { width: number; height: number } {
    if (!maintainAspectRatio) {
      // Never upscale - only downscale if needed
      return {
        width: Math.min(originalWidth, maxWidth),
        height: Math.min(originalHeight, maxHeight)
      };
    }

    // If image is already smaller than max dimensions, return original
    // This prevents upscaling which causes blurriness
    if (originalWidth <= maxWidth && originalHeight <= maxHeight) {
      return { width: originalWidth, height: originalHeight };
    }

    // Calculate aspect ratio
    const aspectRatio = originalWidth / originalHeight;

    let newWidth = maxWidth;
    let newHeight = maxWidth / aspectRatio;

    // Check if height exceeds maxHeight
    if (newHeight > maxHeight) {
      newHeight = maxHeight;
      newWidth = maxHeight * aspectRatio;
    }

    // Final check: never upscale
    newWidth = Math.min(newWidth, originalWidth);
    newHeight = Math.min(newHeight, originalHeight);

    return {
      width: Math.round(newWidth),
      height: Math.round(newHeight)
    };
  }

  /**
   * Generate compressed filename
   */
  private static generateCompressedFilename(
    originalName: string,
    format: string
  ): string {
    const nameWithoutExt = originalName.replace(/\.[^/.]+$/, '');
    return `${nameWithoutExt}.${format}`;
  }

  /**
   * Check if compression is worth it
   * Returns true if the compressed file is significantly smaller
   */
  static isCompressionWorthwhile(result: CompressionResult): boolean {
    // Consider compression worthwhile if it reduces size by at least 10%
    return result.compressionRatio < 0.9;
  }

  /**
   * Get optimal compression options based on file type and use case
   * Adjusts quality based on image size to prevent blurriness in small images
   */
  static getOptimalOptions(
    file: File,
    useCase: 'thumbnail' | 'reference' | 'document' | 'highQuality' = 'reference'
  ): CompressionOptions {
    const preset: CompressionOptions = { ...IMAGE_PRESETS[useCase] };
    const resolveQuality = () => {
      if (typeof preset.quality === 'number') {
        return preset.quality;
      }

      if (typeof DEFAULT_OPTIONS.quality === 'number') {
        return DEFAULT_OPTIONS.quality;
      }

      return 0.85;
    };
    
    // Determine if image is small based on file size
    const isMediumImage = file.size < 1024 * 1024; // < 1MB
    const isSmallImage = file.size < 500 * 1024; // < 500KB
    const isTinyImage = file.size < 200 * 1024; // < 200KB
    
    // For PNG images with transparency, maintain PNG format
    if (file.type === 'image/png') {
      // Keep PNG format for small images or high quality use case
      if (isMediumImage || useCase === 'highQuality' || useCase === 'thumbnail') {
        return {
          ...preset,
          format: 'png',
          quality: 1 // PNG compression is lossless
        };
      }
    }
    
    // Adjust quality based on image size - prioritize sharpness
    if (isTinyImage) {
      // For tiny images, use maximum quality to prevent any blurriness
      preset.quality = 0.98;
      
      // Don't resize tiny images at all
      preset.maxWidth = undefined;
      preset.maxHeight = undefined;
    } else if (isSmallImage) {
      // For small images, use very high quality
      preset.quality = Math.max(0.96, resolveQuality());
      
      // For thumbnails, be less aggressive with resizing
      if (useCase === 'thumbnail') {
        const currentMaxWidth = typeof preset.maxWidth === 'number' ? preset.maxWidth : 0;
        const currentMaxHeight = typeof preset.maxHeight === 'number' ? preset.maxHeight : 0;
        preset.maxWidth = Math.max(800, currentMaxWidth);
        preset.maxHeight = Math.max(800, currentMaxHeight);
      }
    } else if (isMediumImage) {
      // For medium images, still prioritize quality
      preset.quality = Math.max(0.94, resolveQuality());
    } else if (file.size > 5 * 1024 * 1024) {
      // For very large images (> 5MB), use slightly more compression but still prioritize quality
      const currentQuality = resolveQuality();
      preset.quality = Math.max(0.85, currentQuality - 0.05);
    }

    return preset;
  }

  /**
   * Estimate compressed size without actually compressing
   * This is a rough estimate based on typical compression ratios
   */
  static estimateCompressedSize(
    originalSize: number,
    options: CompressionOptions = {}
  ): number {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    
    // Rough estimates based on format and quality
    let estimatedRatio = 1;
    
    if (opts.format === 'jpeg') {
      estimatedRatio = opts.quality! * 0.8; // JPEG typically compresses well
    } else if (opts.format === 'webp') {
      estimatedRatio = opts.quality! * 0.7; // WebP is more efficient
    } else if (opts.format === 'png') {
      estimatedRatio = 0.9; // PNG compression is less predictable
    }

    return Math.round(originalSize * estimatedRatio);
  }
}

// Export convenience functions
export const compressImage = ImageCompressionService.compressImage.bind(ImageCompressionService);
export const compressImages = ImageCompressionService.compressImages.bind(ImageCompressionService);
